import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal,
    ViewChild,
    WritableSignal,
} from "@angular/core";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription, tap } from "rxjs";
import { v4 } from "uuid";

import {
    BUTTON_LAYOUT,
    BUTTON_VARIANT,
    ButtonComponent,
} from "@hermes/aphrodite-button";
import { LOCALE } from "@hermes/core-app-core";
import { TrayFacade } from "@hermes/hermes-web-states-tray-state";
import { Locale, LOCALES } from "@hermes/locale";

import { countries } from "@hermes/utils-generic-constants";

import { UrlUtils } from "@hermes/utils-generic-services-user-interface";

import { GeofilteringTrayAnalyticsService } from "../../services/geofiltering-tray-analytics.service";
import { GeofilteringTrayService } from "../../services/geofiltering-tray.service";

@Component({
    selector: "h-geofiltering-tray",
    imports: [ButtonComponent, TranslateModule],
    templateUrl: "./geofiltering-tray.component.html",
    styleUrls: ["./geofiltering-tray.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeofilteringTrayComponent implements OnInit, OnDestroy {
    private geofilteringService = inject(GeofilteringTrayService);
    private readonly trayFacade = inject(TrayFacade);
    private locale = inject(LOCALE);
    private document = inject(DOCUMENT);
    private urlUtils = inject(UrlUtils);
    private translateService = inject(TranslateService);
    private geofilteringTrayAnalyticsService = inject(
        GeofilteringTrayAnalyticsService,
    );

    public userLocale: WritableSignal<Locale | undefined> = signal(undefined);

    public userLocationCountry: string | null | undefined;

    public totalLocation: number = LOCALES.length;

    public subscription: Subscription = new Subscription();

    @Input({ required: true })
    public userLocationIsoCode!: string | null | undefined;

    @Output()
    public closeBanner: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild("closeButton")
    public closeButton!: ElementRef<HTMLButtonElement>;

    protected readonly BUTTON_VARIANT = BUTTON_VARIANT;
    protected readonly BUTTON_LAYOUT = BUTTON_LAYOUT;
    /**
     * Use in template to personalize an error message according the country (need of $localize)
     */
    public hermesCountrySelector: string = "";

    public ngOnInit(): void {
        this.displayTranslatedCountryName();

        this.subscription.add(
            this.geofilteringService
                .findUserLocale(this.userLocationIsoCode)
                .pipe(
                    tap((userLocale) => {
                        const code = this.userLocationIsoCode?.toUpperCase();
                        if (userLocale) {
                            this.geofilteringTrayAnalyticsService.sendGeoFilteringViewEvent(
                                code,
                            );
                        } else {
                            this.geofilteringTrayAnalyticsService.sendGeoFilteringViewEvent(
                                `NoDelivery_${code}`,
                            );
                        }
                    }),
                )
                .subscribe((userLocale) => {
                    this.userLocale.set(userLocale);
                    this.userLocationCountry = this.getUserLocationCountry(
                        this.userLocationIsoCode,
                    );
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public redirectAlternate() {
        const alternateLink: string | null | undefined = this.document
            .querySelector(
                `link[rel="alternate"][hreflang="${this.userLocale()?.hreflang}"]`,
            )
            ?.getAttribute("href");

        const redirectUrl = alternateLink ?? `${this.userLocale()?.urlPrefix}/`;

        this.geofilteringTrayAnalyticsService.sendGeoFilteringClickEvent(
            `SwitchCountry_${this.userLocationIsoCode?.toUpperCase()}`,
        );

        this.urlUtils.changeLocation(redirectUrl);
    }

    public selectAnotherCountry() {
        this.geofilteringTrayAnalyticsService.sendGeoFilteringClickEvent(
            "SelectOtherCountry",
        );
        this.trayFacade.openTray({
            uuid: v4(),
            name: "country-selector-tray",
            titleKey: "hermes-country-selector-tray.country",
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
        });
    }

    public continueOnCurrentLocation(): void {
        this.trayFacade.closeTray();
        this.geofilteringTrayAnalyticsService.sendGeoFilteringClickEvent(
            `Stay_${this.locale.isoCountryCode?.toUpperCase()}`,
        );
    }

    private getUserLocationCountry(
        userLocationIsoCode: string | null | undefined,
    ): string | undefined {
        return countries.find(
            (country) => country.code === userLocationIsoCode?.toUpperCase(),
        )?.label;
    }

    private displayTranslatedCountryName() {
        this.subscription.add(
            this.translateService
                .get("hermes_country_selector")
                .subscribe((country: string) => {
                    this.hermesCountrySelector = $localize`:@@hermes.header_notification.geo_filtering_banner.no_stay:No, Stay on ${country}:INTERPOLATION: site`;
                }),
        );
    }
}
